export interface PromptsResponse {
  corrector: string;
  generator: string;
}

export interface ValidationIssue {
  text: string;
  issue: string;
  /** "info" is informational-only — never blocks ingestion. */
  severity: "error" | "warning" | "info";
  source?: string;
  location?: string;
}

/** Comprensión Lectora program resolved from a Lectura Primaria doc's `Programa` field. */
export interface ResolvedReadingProgram {
  id: number;
  name: string;
}

/** Per-manipulative-activity summary of an alternatives-template doc. */
export interface AlternativesActivitySummary {
  lesson_position: number;
  activity_title: string;
  alternatives_in_document: number;
  /** > 0 anticipates the ACTIVITIES_HAVE_ALTERNATIVES 409 the upload will raise without a decision. */
  existing_alternatives: number;
}

export interface AlternativesValidateSummary {
  /** null when the course could not be resolved (an ERROR issue explains it). */
  course: { id: number; title: string } | null;
  activities: AlternativesActivitySummary[];
}

export interface ValidateResponse {
  hasErrors: boolean;
  hasWarnings: boolean;
  issues: ValidationIssue[];
  /** Present only when the doc was classified as a Lectura Primaria template. */
  reading?: {
    program: ResolvedReadingProgram | null;
  };
  /** Present only when the doc was classified as an alternatives template (SCRUM-902). */
  alternatives?: AlternativesValidateSummary;
}

/** Returned immediately by POST /content-ingestion */
export interface IngestJobResponse {
  jobId: number;
}

/**
 * Final result when the job reaches COMPLETED status.
 * Lesson, reading and alternatives fields are mutually exclusive depending on
 * which pipeline produced the job (lessons vs. Lectura Primaria vs.
 * actividades alternativas).
 */
export interface IngestionResult {
  lessonsCreated?: number;
  lessonIds?: number[];
  /** Lesson and alternatives pipelines — the reading pipeline result omits it entirely. */
  warnings?: string[];
  readingResourceId?: number;
  readingContentId?: number;
  programId?: number;
  /** Alternatives pipeline only (SCRUM-902). */
  activities_matched?: number;
  alternatives_created?: number;
  /** Pre-existing alternatives retired by the 'replace' decision. */
  replaced_count?: number;
  /** "Lección N > Título" labels left untouched by the 'skip' decision. */
  skipped_activities?: string[];
  /** SCRUM-1532: set when the OD replaced the course's content in place. */
  mode?: "create" | "replace";
  /** Replace mode only: the lessons updated in place, listed in `lessonIds`. */
  lessonsReplaced?: number;
  reviewStatusBefore?: CourseReviewStatus;
  reviewStatusAfter?: CourseReviewStatus;
}

/** A course's editorial review status (SCRUM-1529). */
export type CourseReviewStatus = "POR_REVISAR" | "REVISADA" | "FINALIZADA";

/** Kept for backwards compatibility */
export type IngestResponse = IngestionResult;

export type JobStatus =
  | 'PENDING'
  | 'READING_DOCUMENT'
  | 'VALIDATING'
  | 'BUILDING_REGISTRY'
  | 'INGESTING_LESSONS'
  | 'INGESTING_READING'
  | 'INGESTING_ALTERNATIVES'
  | 'PROCESSING_VIDEOS'
  | 'COMPLETED'
  | 'FAILED';

/** Job object returned by GET /content-ingestion/jobs/:jobId */
export interface IngestionJob {
  id: number;
  document_id: string;
  status: JobStatus;
  progress: number;
  current_lesson?: number | null;
  total_lessons?: number | null;
  current?: number | null;
  total?: number | null;
  message?: string | null;
  result?: IngestionResult | null;
  error_message?: string | null;
  created_at: string;
  updated_at: string;
}

/** Activity listed by the ACTIVITIES_HAVE_ALTERNATIVES 409. */
export interface ConflictedAlternativeActivity {
  lesson_position: number;
  activity_title: string;
  existing_alternatives: number;
}

export interface IngestErrorResponse {
  statusCode: number;
  message: string;
  errors?: string[];
  /**
   * Machine-readable token. `TARGET_HAS_LESSONS` (lessons append) and
   * `ACTIVITIES_HAVE_ALTERNATIVES` (alternatives replace/skip) are the only
   * 409s that open a confirmation dialog — the other conflicts ("ya hay una
   * ingesta en progreso", reading duplicates) must never open one.
   */
  error?: string;
  /** ACTIVITIES_HAVE_ALTERNATIVES only — the conflicted activities. */
  activities?: ConflictedAlternativeActivity[];
  /** CHANGED_IN_PLATFORM only: the course's last upload and last edit. */
  lastUploadAt?: string | null;
  lastPlatformEditAt?: string | null;
  /** CHANGED_IN_PLATFORM: the course, when the API names it. */
  courseId?: number;
  clave?: string | null;
  title?: string;
}

export const TARGET_HAS_LESSONS = "TARGET_HAS_LESSONS";
export const ACTIVITIES_HAVE_ALTERNATIVES = "ACTIVITIES_HAVE_ALTERNATIVES";

/**
 * SCRUM-1532, replacing in place: the OD's lessons, activities or blocks do not
 * match the platform's (422), or the course was edited in the platform after
 * its last upload (409, unless `force`).
 */
export const STRUCTURE_MISMATCH = "STRUCTURE_MISMATCH";
export const CHANGED_IN_PLATFORM = "CHANGED_IN_PLATFORM";
/** SCRUM-1532: the course has a standalone quiz and a video quiz at once. */
export const DOUBLE_QUIZ = "DOUBLE_QUIZ";

/** SCRUM-1532: `replace` swaps the course's content in place, same IDs. */
export interface IngestionFlags {
  replace?: boolean;
  force?: boolean;
}

/** Options of GET /content-ingestion/validate/:documentId (SCRUM-1532). */
export interface ValidateOptions {
  replace?: boolean;
}

/**
 * The admin's ONE GLOBAL decision after an ACTIVITIES_HAVE_ALTERNATIVES 409:
 * 'replace' swaps the existing sets of conflicted activities for the doc's;
 * 'skip' leaves conflicted activities untouched and processes the rest.
 */
export type OnExistingAlternatives = "replace" | "skip";
